import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "../Common/Configurations/AuthContext";
import { useNavigate } from "react-router-dom";
import config from "../Common/Configurations/APIConfig";
import apiService from "../Common/Configurations/APIService";
import SupplierHeader from "./SupplierHeader";
import Header from "../Common/Layout/Header";
import SupplierTabs from "./SupplierTabs";
import moment from "moment";
import "./SupplierVisaRequests.css";

const SupplierVisaRequests = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  // Use dynamic supplier ID from AuthContext (companyId)
  const supplierId = user?.cid;

  const [visaRequests, setVisaRequests] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showApplicantsModal, setShowApplicantsModal] = useState(false);
  const [editingApplicant, setEditingApplicant] = useState(null);
  const [statusUpdateData, setStatusUpdateData] = useState({
    visaApplicantStatus: "",
    approvedVisaFile: null,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isInitialMount = useRef(true);

  // Status options for dropdown
  const statusOptions = [
    { value: "PENDING", label: "Pending" },
    { value: "PROCESSING", label: "Processing" },
    { value: "APPROVED", label: "Approved" },
    { value: "REJECTED", label: "Rejected" },
    { value: "DOCUMENT_PENDING", label: "Document Pending" },
    { value: "DOCUMENTS_MISSING", label: "Documents Missing" },
  ];

  // Fetch visa requests by supplier
  const GetVisaRequestsBySupplier = async () => {
    setIsLoading(true);
    try {
      const url = `${config.GETVISAREQUESTSBYSUPPLIER}/${supplierId}`;
      const res = await apiService.getAll(url);

      if (res.success) {
        setVisaRequests(res.data || []);
      } else {
        setVisaRequests([]);
      }
    } catch (error) {
      console.error("Error fetching visa requests:", error);
      setVisaRequests([]);
    } finally {
      setIsLoading(false);
    }
  };

  // View applicants for a specific visa request
  const handleViewApplicants = (visaRequest) => {
    setSelectedRequest(visaRequest);
    setShowApplicantsModal(true);
    setEditingApplicant(null);
    setStatusUpdateData({
      visaApplicantStatus: "",
      approvedVisaFile: null,
    });
  };

  // Start editing an applicant
  const handleEditApplicant = (applicant) => {
    setEditingApplicant(applicant);
    setStatusUpdateData({
      visaApplicantStatus: applicant.visaApplicantStatus || "PENDING",
      approvedVisaFile: null,
    });
  };

  // Cancel editing
  const handleCancelEdit = () => {
    setEditingApplicant(null);
    setStatusUpdateData({
      visaApplicantStatus: "",
      approvedVisaFile: null,
    });
  };

  // Handle status change
  const handleStatusChange = (e) => {
    setStatusUpdateData((prev) => ({
      ...prev,
      visaApplicantStatus: e.target.value,
    }));
  };

  // Handle file upload
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setStatusUpdateData((prev) => ({
      ...prev,
      approvedVisaFile: file,
    }));
  };

  // Update applicant status and upload visa document
  const handleUpdateApplicant = async () => {
    if (!editingApplicant || !statusUpdateData.visaApplicantStatus) {
      alert("Please select a status");
      return;
    }

    setIsSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("VisaApplicantId", editingApplicant.visaApplicantId);
      formData.append(
        "VisaApplicantStatus",
        statusUpdateData.visaApplicantStatus
      );

      if (statusUpdateData.approvedVisaFile) {
        formData.append("ApprovedVisaFile", statusUpdateData.approvedVisaFile);
      }

      // Update the applicant
      const updateResponse = await apiService.update(
        `${config.UPDATEAPPLICANTSTATUSANDFILE}/${editingApplicant.visaApplicantId}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (updateResponse.success) {
        // Refresh the data
        await GetVisaRequestsBySupplier();

        // Update the selected request in modal
        if (selectedRequest) {
          const updatedRequest = visaRequests.find(
            (req) => req.visaRequestId === selectedRequest.visaRequestId
          );
          if (updatedRequest) {
            setSelectedRequest(updatedRequest);
          }
        }

        // Reset editing state
        setEditingApplicant(null);
        setStatusUpdateData({
          visaApplicantStatus: "",
          approvedVisaFile: null,
        });

        alert("Applicant status updated successfully!");
      } else {
        alert("Failed to update applicant status");
      }
    } catch (error) {
      console.error("Error updating applicant:", error);
      alert("Error updating applicant status");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Status badge classes
  const getStatusBadgeClass = (status) => {
    if (!status) return "badge bg-secondary";

    switch (status.toUpperCase()) {
      case "APPROVED":
        return "badge bg-success";
      case "PENDING":
      case "PROCESSING":
      case "DOCUMENT_PENDING":
        return "badge bg-warning text-dark";
      case "REJECTED":
        return "badge bg-danger";
      case "SUBMITTED":
      case "NEW":
        return "badge bg-info";
      case "DOCUMENTS_MISSING":
        return "badge bg-danger";
      default:
        return "badge bg-secondary";
    }
  };

  // Applicant status badge
  const getApplicantStatusBadgeClass = (status) => {
    if (!status) return "badge bg-secondary";

    switch (status.toUpperCase()) {
      case "APPROVED":
        return "badge bg-success";
      case "PENDING":
        return "badge bg-warning text-dark";
      case "REJECTED":
        return "badge bg-danger";
      case "PROCESSING":
        return "badge bg-info";
      default:
        return "badge bg-secondary";
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return moment(dateString).format("DD-MMM-YYYY");
  };

  // Get gender display text
  const getGenderDisplay = (gender) => {
    if (!gender) return "-";
    return gender.charAt(0).toUpperCase() + gender.slice(1).toLowerCase();
  };

  // Check if passport is expired
  const isPassportExpired = (expiryDate) => {
    if (!expiryDate) return false;
    return moment(expiryDate).isBefore(moment());
  };

  // Handle document download
  const handleDownload = async (url, filename) => {
    if (!url) {
      console.error("No URL provided for download");
      return;
    }

    try {
      // Fetch the file from the URL
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Convert the response to a blob
      const blob = await response.blob();

      // Create a temporary URL for the blob
      const blobUrl = window.URL.createObjectURL(blob);

      // Create a temporary anchor element to trigger the download
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = filename || "document";

      // Append to body, click, and remove
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up the blob URL
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Error downloading file:", error);
      // Fallback: open in new tab if download fails
      window.open(url, "_blank");
    }
  };

  // Helper function to get file extension from URL
  const getFileExtension = (url) => {
    if (!url) return "jpg";
    const filename = url.split("/").pop();
    const extension = filename.split(".").pop();
    return extension || "jpg";
  };

  // Helper function to get document name from URL
  const getDocumentName = (url, defaultName = "Document") => {
    if (!url) return defaultName;
    const filename = url.split("/").pop();
    // Remove UUIDs and get a clean name
    const cleanName = filename
      .replace(
        /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}_/g,
        ""
      )
      .replace(/_/g, " ")
      .split(".")[0];
    return cleanName || defaultName;
  };

  // Check if applicant has any documents
  const hasDocuments = (applicant) => {
    return (
      applicant.profileSrc ||
      applicant.passportFrontSrc ||
      applicant.passportBackSrc ||
      applicant.approvedVisaSrc ||
      (applicant.documents && applicant.documents.length > 0)
    );
  };

  // Refresh data function
  const handleRefresh = () => {
    GetVisaRequestsBySupplier();
  };

  useEffect(() => {
    if (!loading && !user) {
      navigate("/");
    }
  }, [user, loading, navigate]);

  useEffect(() => {
    if (!user) return;

    if (isInitialMount.current) {
      isInitialMount.current = false;
      GetVisaRequestsBySupplier();
      return;
    }
  }, [user]);

  if (loading) return <p>Loading...</p>;
  if (!user) {
    navigate("/");
  }

  return (
    <div id="layout-wrapper">
      {/* <SupplierHeader User={user} />  */}
      <Header User={user} />
      <div className="main-content">
        <div className="page-content">
          <div className="container-fluid">
            <div className="card">
              <div className="p-2">
                {/* <SupplierTabs pageName="REQUESTS" /> */}
              </div>
              <div className="card-body">
                <div className="row mb-3">
                  <div className="col-12 d-flex justify-content-between align-items-center">
                    <h5 className="card-title mb-0">
                      Visa Requests for Supplier
                      {visaRequests.length > 0 && (
                        <span className="text-muted ms-2">
                          ({visaRequests.length} requests found)
                        </span>
                      )}
                    </h5>
                    <button
                      className="btn btn-sm btn-primary"
                      onClick={handleRefresh}
                      disabled={isLoading}
                    >
                      <i className="ri-refresh-line align-bottom me-1"></i>
                      Refresh
                    </button>
                  </div>
                </div>

                {isLoading ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-2">Loading visa requests...</p>
                  </div>
                ) : (
                  <>
                    <div className="table-responsive">
                      <table className="table table-centered table-nowrap mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>Company Name</th>
                            <th>Invoice No</th>
                            <th>Visa Details</th>
                            <th>Applicant Name</th>
                            <th>No. of Applicants</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {visaRequests && visaRequests.length > 0 ? (
                            visaRequests.map((request, index) => (
                              <tr key={request.visaRequestId}>
                                <td className="fw-semibold">
                                  {request.companyName || "-"}
                                </td>
                                <td className="fw-semibold">
                                  {request.invoiceNo || "-"}
                                </td>
                                <td>
                                  <div
                                    className="fw-semibold text-wrap"
                                    style={{ maxWidth: "200px" }}
                                  >
                                    {request.visaName || "-"}
                                    {request.countryName && (
                                      <small className="text-muted d-block">
                                        {request.countryName}
                                      </small>
                                    )}
                                  </div>
                                </td>
                                <td>
                                  <div className="fw-semibold">
                                    {request.applicantName || "-"}
                                  </div>
                                </td>
                                <td>
                                  <div className="text-danger fw-semibold">
                                    {request.applicants?.length || 0}
                                  </div>
                                </td>
                                <td>
                                  <button
                                    className="btn btn-sm btn-primary"
                                    onClick={() =>
                                      handleViewApplicants(request)
                                    }
                                    disabled={!request.applicants?.length}
                                  >
                                    <i className="ri-group-line align-bottom me-1"></i>
                                    View Applicants
                                    {request.applicants?.length > 0 && (
                                      <span className="ms-1">
                                        ({request.applicants.length})
                                      </span>
                                    )}
                                  </button>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="7" className="text-center py-4">
                                No visa requests found for this supplier
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Applicants Modal */}
      {showApplicantsModal && selectedRequest && (
        <div
          className="modal fade show d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-xl modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  Applicants for {selectedRequest.visaName}
                  <small className="text-muted ms-2">
                    ({selectedRequest.applicants?.length || 0} applicants)
                  </small>
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowApplicantsModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <div className="row mb-3">
                  <div className="col-md-4">
                    <strong>Invoice No:</strong>{" "}
                    {selectedRequest.invoiceNo || "-"}
                  </div>
                  <div className="col-md-4">
                    <strong>Country:</strong>{" "}
                    {selectedRequest.countryName || "-"}
                  </div>
                  <div className="col-md-4">
                    <strong>Main Applicant:</strong>{" "}
                    {selectedRequest.applicantName || "-"}
                  </div>
                </div>

                <div className="table-responsive">
                  <table className="table table-bordered table-hover">
                    <thead className="table-light">
                      <tr>
                        <th>S.No</th>
                        <th>Applicant Details</th>
                        <th>Passport Details</th>
                        <th>Status</th>
                        <th>Documents</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedRequest.applicants &&
                      selectedRequest.applicants.length > 0 ? (
                        selectedRequest.applicants.map((applicant, index) => (
                          <tr key={applicant.visaApplicantId}>
                            <td className="text-center">{index + 1}</td>

                            {/* Applicant Details Column */}
                            <td>
                              <div className="mb-1">
                                <strong className="text-primary">
                                  {applicant.firstName} {applicant.lastName}
                                </strong>
                              </div>
                              <div className="small text-muted">
                                <div>
                                  <strong>Gender:</strong>{" "}
                                  {getGenderDisplay(applicant.gender)}
                                </div>
                                <div>
                                  <strong>DOB:</strong>{" "}
                                  {formatDate(applicant.dateOfBirth)}
                                </div>
                                <div>
                                  <strong>Traveler Type:</strong>{" "}
                                  <span className="badge bg-info">
                                    {applicant.travelerType || "-"}
                                  </span>
                                </div>
                              </div>
                            </td>

                            {/* Passport Details Column */}
                            <td>
                              <div className="mb-2">
                                <div className="d-flex align-items-center gap-2">
                                  <strong>Passport No:</strong>
                                  <code className="bg-light px-2 py-1 rounded">
                                    {applicant.passportNumber || "-"}
                                  </code>
                                </div>
                              </div>
                              <div className="small text-muted">
                                <div className="mb-1">
                                  <strong>Nationality:</strong>{" "}
                                  <span className="fw-semibold text-dark">
                                    {applicant.nationality || "-"}
                                  </span>
                                </div>
                                <div>
                                  <strong>Issued:</strong>{" "}
                                  {formatDate(applicant.passportIssueDate)}
                                </div>
                                <div>
                                  <strong>Expiry:</strong>{" "}
                                  <span
                                    className={
                                      isPassportExpired(
                                        applicant.passportExpireDate
                                      )
                                        ? "text-danger fw-semibold"
                                        : "text-muted"
                                    }
                                  >
                                    {formatDate(applicant.passportExpireDate)}
                                    {isPassportExpired(
                                      applicant.passportExpireDate
                                    ) && (
                                      <span className="badge bg-danger ms-1">
                                        Expired
                                      </span>
                                    )}
                                  </span>
                                </div>
                                <div>
                                  <strong>Country:</strong>{" "}
                                  {applicant.passportCountry || "-"}
                                </div>
                              </div>
                            </td>

                            {/* Status Column */}
                            <td className="align-middle">
                              <span
                                className={getApplicantStatusBadgeClass(
                                  applicant.visaApplicantStatus
                                )}
                              >
                                {applicant.visaApplicantStatus || "PENDING"}
                              </span>
                            </td>

                            {/* Documents Column */}
                            <td>
                              <div className="d-flex flex-wrap gap-2">
                                {/* Profile Photo */}
                                {applicant.profileSrc && (
                                  <div className="document-action">
                                    <small className="text-muted d-block mb-1">
                                      Profile Photo
                                    </small>
                                    <div
                                      className="btn-group btn-group-sm"
                                      role="group"
                                    >
                                      <button
                                        className="btn btn-primary"
                                        onClick={() =>
                                          window.open(
                                            applicant.profileSrc,
                                            "_blank"
                                          )
                                        }
                                        title="View Profile Photo"
                                      >
                                        <i className="ri-eye-line"></i>
                                      </button>
                                      <button
                                        className="btn btn-success"
                                        onClick={() =>
                                          handleDownload(
                                            applicant.profileSrc,
                                            `Profile_${applicant.firstName}_${
                                              applicant.lastName
                                            }.${getFileExtension(
                                              applicant.profileSrc
                                            )}`
                                          )
                                        }
                                        title="Download Profile Photo"
                                      >
                                        <i className="ri-download-line"></i>
                                      </button>
                                    </div>
                                  </div>
                                )}

                                {/* Passport Front */}
                                {applicant.passportFrontSrc && (
                                  <div className="document-action">
                                    <small className="text-muted d-block mb-1">
                                      Passport Front
                                    </small>
                                    <div
                                      className="btn-group btn-group-sm"
                                      role="group"
                                    >
                                      <button
                                        className="btn btn-primary"
                                        onClick={() =>
                                          window.open(
                                            applicant.passportFrontSrc,
                                            "_blank"
                                          )
                                        }
                                        title="View Passport Front"
                                      >
                                        <i className="ri-eye-line"></i>
                                      </button>
                                      <button
                                        className="btn btn-success"
                                        onClick={() =>
                                          handleDownload(
                                            applicant.passportFrontSrc,
                                            `PassportFront_${
                                              applicant.firstName
                                            }_${
                                              applicant.lastName
                                            }.${getFileExtension(
                                              applicant.passportFrontSrc
                                            )}`
                                          )
                                        }
                                        title="Download Passport Front"
                                      >
                                        <i className="ri-download-line"></i>
                                      </button>
                                    </div>
                                  </div>
                                )}

                                {/* Passport Back */}
                                {applicant.passportBackSrc && (
                                  <div className="document-action">
                                    <small className="text-muted d-block mb-1">
                                      Passport Back
                                    </small>
                                    <div
                                      className="btn-group btn-group-sm"
                                      role="group"
                                    >
                                      <button
                                        className="btn btn-primary"
                                        onClick={() =>
                                          window.open(
                                            applicant.passportBackSrc,
                                            "_blank"
                                          )
                                        }
                                        title="View Passport Back"
                                      >
                                        <i className="ri-eye-line"></i>
                                      </button>
                                      <button
                                        className="btn btn-success"
                                        onClick={() =>
                                          handleDownload(
                                            applicant.passportBackSrc,
                                            `PassportBack_${
                                              applicant.firstName
                                            }_${
                                              applicant.lastName
                                            }.${getFileExtension(
                                              applicant.passportBackSrc
                                            )}`
                                          )
                                        }
                                        title="Download Passport Back"
                                      >
                                        <i className="ri-download-line"></i>
                                      </button>
                                    </div>
                                  </div>
                                )}

                                {/* Approved Visa */}
                                {applicant.approvedVisaSrc && (
                                  <div className="document-action">
                                    <small className="text-muted d-block mb-1">
                                      Approved Visa
                                    </small>
                                    <div
                                      className="btn-group btn-group-sm"
                                      role="group"
                                    >
                                      <button
                                        className="btn btn-primary"
                                        onClick={() =>
                                          window.open(
                                            applicant.approvedVisaSrc,
                                            "_blank"
                                          )
                                        }
                                        title="View Approved Visa"
                                      >
                                        <i className="ri-eye-line"></i>
                                      </button>
                                      <button
                                        className="btn btn-success"
                                        onClick={() =>
                                          handleDownload(
                                            applicant.approvedVisaSrc,
                                            `Visa_${applicant.firstName}_${
                                              applicant.lastName
                                            }.${getFileExtension(
                                              applicant.approvedVisaSrc
                                            )}`
                                          )
                                        }
                                        title="Download Approved Visa"
                                      >
                                        <i className="ri-download-line"></i>
                                      </button>
                                    </div>
                                  </div>
                                )}

                                {/* Additional Documents */}
                                {applicant.documents &&
                                  applicant.documents.length > 0 && (
                                    <>
                                      {applicant.documents.map(
                                        (doc, docIndex) => (
                                          <div
                                            key={doc.visaApplicantDocumentId}
                                            className="document-action"
                                          >
                                            <small className="text-muted d-block mb-1">
                                              {`Additional Doc ${docIndex + 1}`}
                                            </small>
                                            <div
                                              className="btn-group btn-group-sm"
                                              role="group"
                                            >
                                              <button
                                                className="btn btn-primary"
                                                onClick={() =>
                                                  window.open(
                                                    doc.imageSrc,
                                                    "_blank"
                                                  )
                                                }
                                                title={`View ${getDocumentName(
                                                  doc.imageSrc
                                                )}`}
                                              >
                                                <i className="ri-eye-line"></i>
                                              </button>
                                              <button
                                                className="btn btn-success"
                                                onClick={() =>
                                                  handleDownload(
                                                    doc.imageSrc,
                                                    `${getDocumentName(
                                                      doc.imageSrc,
                                                      "Additional_Document"
                                                    )}_${applicant.firstName}_${
                                                      applicant.lastName
                                                    }.${getFileExtension(
                                                      doc.imageSrc
                                                    )}`
                                                  )
                                                }
                                                title={`Download ${getDocumentName(
                                                  doc.imageSrc
                                                )}`}
                                              >
                                                <i className="ri-download-line"></i>
                                              </button>
                                            </div>
                                          </div>
                                        )
                                      )}
                                    </>
                                  )}

                                {/* No Documents Message */}
                                {!hasDocuments(applicant) && (
                                  <span className="text-muted small">
                                    No documents
                                  </span>
                                )}
                              </div>
                            </td>

                            {/* Actions Column */}
                            <td className="align-middle">
                              {editingApplicant?.visaApplicantId ===
                              applicant.visaApplicantId ? (
                                <div className="d-flex flex-column gap-2">
                                  {/* Status Dropdown */}
                                  <select
                                    className="form-select form-select-sm"
                                    value={statusUpdateData.visaApplicantStatus}
                                    onChange={handleStatusChange}
                                  >
                                    <option value="">Select Status</option>
                                    {statusOptions.map((option) => (
                                      <option
                                        key={option.value}
                                        value={option.value}
                                      >
                                        {option.label}
                                      </option>
                                    ))}
                                  </select>

                                  {/* File Upload */}
                                  <div>
                                    <label className="form-label small mb-1">
                                      Approved Visa Document:
                                    </label>
                                    <input
                                      type="file"
                                      className="form-control form-control-sm"
                                      accept=".pdf,.jpg,.jpeg,.png"
                                      onChange={handleFileChange}
                                    />
                                    <small className="text-muted">
                                      Upload visa document when status is
                                      APPROVED
                                    </small>
                                  </div>

                                  {/* Action Buttons */}
                                  <div className="d-flex gap-1">
                                    <button
                                      className="btn btn-success btn-sm"
                                      onClick={handleUpdateApplicant}
                                      disabled={
                                        isSubmitting ||
                                        !statusUpdateData.visaApplicantStatus
                                      }
                                    >
                                      {isSubmitting ? (
                                        <div
                                          className="spinner-border spinner-border-sm"
                                          role="status"
                                        >
                                          <span className="visually-hidden">
                                            Updating...
                                          </span>
                                        </div>
                                      ) : (
                                        <i className="ri-check-line"></i>
                                      )}
                                      Update
                                    </button>
                                    <button
                                      className="btn btn-secondary btn-sm"
                                      onClick={handleCancelEdit}
                                      disabled={isSubmitting}
                                    >
                                      <i className="ri-close-line"></i>
                                      Cancel
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <button
                                  className="btn btn-warning btn-sm"
                                  onClick={() => handleEditApplicant(applicant)}
                                  title="Edit Status & Upload Visa"
                                >
                                  <i className="ri-edit-line"></i>
                                  Edit
                                </button>
                              )}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="6" className="text-center py-3">
                            No applicants found for this visa request
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={() => setShowApplicantsModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SupplierVisaRequests;
